import { Badge } from 'react-bootstrap';
import { SalvModal } from '@salvapp/ui-components';
import React, { useMemo, useState } from 'react';
import { FieldArray, Form, Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { FormikState } from 'formik/dist/types';
import './UsableFields.scss';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { customListUsableFieldApi } from '../../apis';
import { InternalApiUsableField, UsableFieldType } from './usableFieldTypes';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { dropdownSchema } from '../../common/validation/helper';
import { showFeedback } from '../../feedback/feedbackReducer';
import { useDispatch, useSelector } from 'react-redux';
import { InternalApiCustomListScreeningFieldType } from '../../api-internal';
import { FieldArrayRenderProps } from 'formik/dist/FieldArray';
import {
  SalvBanner,
  SalvBannerType,
  SalvBannerAccent,
  SalvButton,
  SalvDropdown,
  SalvTable,
  SalvTextField,
  Size,
} from '@salvapp/ui-components';
import { ErrorResponse } from '../../common/errors/helpers';
import {
  addUsableMonitoringFieldsSuccess,
  addUsableScreeningFieldsSuccess,
  fetchUsableMonitoringFieldsSuccess,
  fetchUsableScreeningFieldsSuccess,
} from '../customListReducer';
import { useParams } from 'react-router';
import { createColumnHelper, ExpandedStateList } from '@tanstack/react-table';
import { AppState } from '../../store/store';
import MonitoringFieldPreview from './MonitoringFieldPreview';
import useUsableFieldParts from './hooks/useUsableFieldParts';

type UsableFieldFormType = Omit<InternalApiUsableField, 'path' | 'type'> & {
  usableFieldParts: DropdownOption[];
  type: DropdownOption | undefined;
};

type UsableFieldsFormType = {
  usableFields: UsableFieldFormType[];
};

const isFormikValue = (usableField: UsableFieldFormType | InternalApiUsableField): usableField is UsableFieldFormType =>
  !usableField.id;

interface Props {
  type: UsableFieldType;
  usableFields: InternalApiUsableField[];
}

export const UsableFieldsModal = ({ type, usableFields }: Props) => {
  const customListId = useParams().customListId!;
  const [showModal, setShowModal] = useState(false);
  const dispatch = useDispatch();
  const [bannerMessages, setBannerMessages] = useState('');
  const schemaFields = useSelector((state: AppState) => state.customList.schemaFields);
  const additionalDataSchemaFields = schemaFields.filter((field) => !field.parentId);

  const validationSchema = Yup.object({
    usableFields: Yup.array()
      .of(
        Yup.object({
          id: Yup.string(),
          name: Yup.string().required('Name is required'),
          usableFieldParts: Yup.array().of(dropdownSchema).required(),
          code: Yup.string()
            .matches(/^\w+$/i, {
              message: 'Code must contain letters, numbers or underscores',
            })
            .required(''),
          type: dropdownSchema.default(undefined).nullable(),
          rule: Yup.string(),
          additionalSchemaFields: Yup.array(),
        })
      )
      .required(),
  }) as Yup.Schema<UsableFieldsFormType>;

  const initialValues: UsableFieldsFormType = {
    usableFields: [],
  };

  const initialFormikState: Partial<FormikState<UsableFieldsFormType>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const submit = async (
    values: UsableFieldsFormType,
    { setSubmitting, resetForm }: FormikHelpers<UsableFieldsFormType>
  ) => {
    setBannerMessages('');
    const formikUsableFields = values.usableFields;
    try {
      await confirmRemoveExistingUsableField();
      const newUsableFields = await Promise.all(
        formikUsableFields.map(async (field) => {
          const { usableFieldParts, name, code } = field;
          const path = usableFieldParts.map((field) => field.value).join(' ');

          return type === UsableFieldType.SCREENING
            ? await customListUsableFieldApi.addScreeningField({
                customListId,
                internalApiCustomListScreeningField: {
                  name,
                  path,
                  code,
                  type: field.type?.value as InternalApiCustomListScreeningFieldType,
                  rule: field.rule,
                },
              })
            : await customListUsableFieldApi.addMonitoringField({
                customListId,
                internalApiCustomListMonitoringField: {
                  name,
                  path,
                  additionalSchemaFields: field.additionalSchemaFields,
                  code,
                },
              });
        })
      );

      if (type === UsableFieldType.SCREENING) {
        dispatch(addUsableScreeningFieldsSuccess(newUsableFields));
      } else {
        dispatch(addUsableMonitoringFieldsSuccess(newUsableFields));
      }

      resetForm(initialFormikState);
      setShowModal(false);
      dispatch(showFeedback({ message: 'Configuration updated!', success: true }));
    } catch (e) {
      await handleError(e);
    }

    setSubmitting(false);
  };

  const handleError = async (e: unknown) => {
    if (type === UsableFieldType.SCREENING) {
      dispatch(fetchUsableScreeningFieldsSuccess(await customListUsableFieldApi.getScreeningFields({ customListId })));
    } else {
      dispatch(
        fetchUsableMonitoringFieldsSuccess(await customListUsableFieldApi.getMonitoringFields({ customListId }))
      );
    }
    setFieldsToBeDeleted([]);
    const res = e as Response;
    if (res.status === 400) {
      const { message } = (await res.json()) as ErrorResponse;
      setBannerMessages(message);
    }
  };

  const handleClose = (resetForm?: (nextState?: Partial<FormikState<UsableFieldsFormType>>) => void) => {
    if (resetForm) {
      resetForm(initialFormikState);
    }
    setShowModal(false);
    setBannerMessages('');
    setFieldsToBeDeleted([]);
  };

  const [fieldsToBeDeleted, setFieldsToBeDeleted] = useState<string[]>([]);

  const removeExistingUsableField = (fieldId: string) => {
    setFieldsToBeDeleted((fieldsToBeDeleted) => fieldsToBeDeleted.concat(fieldId));
  };

  const confirmRemoveExistingUsableField = async () => {
    await Promise.all(
      fieldsToBeDeleted.map(async (fieldId) => {
        type === UsableFieldType.SCREENING
          ? await customListUsableFieldApi.deleteScreeningField({
              customListId,
              fieldId,
            })
          : await customListUsableFieldApi.deleteMonitoringField({
              customListId,
              fieldId,
            });
      })
    );

    if (type === UsableFieldType.SCREENING) {
      dispatch(
        fetchUsableScreeningFieldsSuccess(
          usableFields.filter((usableField) => !fieldsToBeDeleted.includes(usableField.id!))
        )
      );
    } else {
      dispatch(
        fetchUsableMonitoringFieldsSuccess(
          usableFields.filter((usableField) => !fieldsToBeDeleted.includes(usableField.id!))
        )
      );
    }

    setFieldsToBeDeleted([]);
  };

  const title =
    type === UsableFieldType.SCREENING ? 'Edit usable fields in screening' : 'Edit usable fields in monitoring';

  const additionalSchemaFieldOptions = schemaFields
    .filter((field) => !field.parentId)
    .map((schemaField) => {
      return {
        key: schemaField.id!,
        value: schemaField.id!,
        label: schemaField.fieldName,
      };
    });
  const screeningFieldTypeOptions: DropdownOption[] = [
    {
      key: InternalApiCustomListScreeningFieldType.INDIVIDUAL_PERSON_NAME,
      value: InternalApiCustomListScreeningFieldType.INDIVIDUAL_PERSON_NAME,
      label: 'Individual Person Name',
    },
    {
      key: InternalApiCustomListScreeningFieldType.LEGAL_PERSON_NAME,
      value: InternalApiCustomListScreeningFieldType.LEGAL_PERSON_NAME,
      label: 'Legal Person Name',
    },
  ];

  return (
    <Formik onSubmit={submit} validationSchema={validationSchema} enableReinitialize initialValues={initialValues}>
      {({ isSubmitting, errors, values, resetForm, setFieldValue }: FormikProps<UsableFieldsFormType>) => {
        const columnHelper = createColumnHelper<InternalApiUsableField | UsableFieldFormType>();
        const columns = useMemo(
          () => [
            columnHelper.accessor('name', {
              header: 'Name',
              cell: ({ row }) => {
                const usableField = row.original;
                const editableRowIndex = row.index - usableFields.length;

                return isFormikValue(usableField) ? (
                  <SalvTextField
                    size={Size.SMALL}
                    ariaLabel={`Name ${editableRowIndex + 1}`}
                    value={usableField.name}
                    onChange={(value) => setFieldValue(`usableFields.${editableRowIndex}.name`, value)}
                  />
                ) : (
                  usableField.name
                );
              },
            }),
            columnHelper.accessor('code', {
              header: 'Code',
              cell: ({ row, table }) => {
                const usableField = row.original;
                const editableRowIndex = row.index - usableFields.length;
                const { errors } = table.options.meta as { errors: FormikErrors<UsableFieldsFormType> };
                const codeError = (errors?.usableFields?.[editableRowIndex] as UsableFieldFormType)?.code;
                return isFormikValue(usableField) ? (
                  <>
                    <SalvTextField
                      size={Size.SMALL}
                      ariaLabel={`Code ${editableRowIndex + 1}`}
                      value={usableField.code}
                      onChange={(value) => setFieldValue(`usableFields.${editableRowIndex}.code`, value.toUpperCase())}
                    />
                    {codeError && <div className={'salv-error-message'}>{codeError}</div>}
                  </>
                ) : (
                  usableField.code
                );
              },
            }),
            columnHelper.accessor('path', {
              header: 'Field',
              cell: ({ row }) => {
                const usableField = row.original;
                const editableRowIndex = row.index - usableFields.length;
                const { setFirstSelectedPart, usableFieldParts } = useUsableFieldParts(customListId);

                return isFormikValue(usableField) ? (
                  <SalvDropdown
                    size={Size.SMALL}
                    ariaLabel={`Field ${editableRowIndex + 1}`}
                    isMulti
                    options={usableFieldParts}
                    value={usableField.usableFieldParts}
                    onChange={(selectedList: DropdownOption[]) => {
                      setFieldValue(`usableFields.${editableRowIndex}.usableFieldParts`, selectedList);
                      if (selectedList.length === 1) {
                        setFirstSelectedPart(selectedList[0]);
                      } else if (selectedList.length === 0) {
                        setFirstSelectedPart(undefined);
                      }
                    }}
                  />
                ) : (
                  <Badge bg='primary'>{usableField.path}</Badge>
                );
              },
            }),
            columnHelper.accessor('additionalSchemaFields', {
              header: 'Additional data',
              cell: ({ row }) => {
                const usableField = row.original;
                const editableRowIndex = row.index - usableFields.length;

                const selectedAdditionalSchemaFields = usableField.additionalSchemaFields
                  ?.map((schemaFieldId) =>
                    schemaFields
                      .filter((field) => !field.parentId)
                      .find((schemaField) => schemaField.id === schemaFieldId)!
                  )
                  .map((additionalSchemaField) => {
                    return {
                      key: additionalSchemaField.id!,
                      value: additionalSchemaField.id!,
                      label: additionalSchemaField.fieldName,
                    };
                  });

                return isFormikValue(usableField) ? (
                  <SalvDropdown
                    size={Size.SMALL}
                    ariaLabel={`Additional data ${editableRowIndex + 1}`}
                    isMulti
                    options={additionalSchemaFieldOptions}
                    value={selectedAdditionalSchemaFields}
                    onChange={(selectedFields) =>
                      setFieldValue(
                        `usableFields.${editableRowIndex}.additionalSchemaFields`,
                        selectedFields.map((schemaField) => schemaField.value)
                      )
                    }
                  />
                ) : (
                  additionalDataSchemaFields.length && (
                    <Badge bg='primary'>
                      {(usableField.additionalSchemaFields as string[] | undefined)
                        ?.map((schemaFieldId) => {
                          const schemaField = additionalDataSchemaFields.find(
                            (schemaField) => schemaField.id === schemaFieldId
                          )!;

                          return schemaField.fieldName;
                        })
                        .join(' ')}
                    </Badge>
                  )
                );
              },
            }),
            columnHelper.accessor('type', {
              header: 'Type',
              cell: ({ row }) => {
                const usableField = row.original;
                const editableRowIndex = row.index - usableFields.length;

                return isFormikValue(usableField) ? (
                  <SalvDropdown
                    size={Size.SMALL}
                    ariaLabel={`Type ${editableRowIndex + 1}`}
                    options={screeningFieldTypeOptions}
                    value={usableField.type}
                    onChange={(selected: DropdownOption[]) => {
                      setFieldValue(`usableFields.${editableRowIndex}.type`, selected[0]);
                    }}
                  />
                ) : (
                  usableField.type
                );
              },
            }),
            columnHelper.accessor('rule', {
              header: 'Type rule',
              cell: ({ row }) => {
                const usableField = row.original;
                const editableRowIndex = row.index - usableFields.length;

                return isFormikValue(usableField) ? (
                  <SalvTextField
                    size={Size.SMALL}
                    ariaLabel={`Type rule  ${editableRowIndex + 1}`}
                    value={usableField.rule}
                    onChange={(value) => setFieldValue(`usableFields.${editableRowIndex}.rule`, value)}
                  />
                ) : (
                  usableField.rule
                );
              },
            }),
            columnHelper.display({
              id: 'actions',
              size: 0,
              cell: ({ row, table }) => {
                const usableField = row.original;
                const editableRowIndex = row.index - usableFields.length;
                const { arrayHelpers } = table.options.meta as { arrayHelpers: FieldArrayRenderProps };

                return (
                  <SalvButton
                    label='Remove'
                    onClick={() =>
                      isFormikValue(usableField)
                        ? arrayHelpers.remove(editableRowIndex)
                        : removeExistingUsableField(row.original.id!)
                    }
                    size={Size.SMALL}
                    variant='danger-outline'
                  />
                );
              },
            }),
          ],
          [type, additionalDataSchemaFields]
        );

        const data = useMemo(
          () =>
            (
              usableFields.filter((usableField) => !fieldsToBeDeleted.includes(usableField.id!)) as (
                InternalApiUsableField | UsableFieldFormType
              )[]
            ).concat(values.usableFields),
          [usableFields, fieldsToBeDeleted, values.usableFields]
        );

        const expanded = useMemo(
          () =>
            type === UsableFieldType.MONITORING
              ? data.reduce((expandedState, field, index) => {
                  expandedState[index.toString()] = true;

                  return expandedState;
                }, {} as ExpandedStateList)
              : {},
          [data]
        );

        return (
          <>
            <SalvButton label={'Edit'} variant='muted' size={Size.SMALL} onClick={() => setShowModal(true)} />
            <SalvModal dialogClassName='usable-fields-modal' show={showModal} onHide={handleClose}>
              <Form>
                <SalvModal.Header closeButton>{title}</SalvModal.Header>
                <SalvModal.Body>
                  {bannerMessages &&
                    bannerMessages
                      .split('\n')
                      .map((message, index) => (
                        <SalvBanner
                          key={index}
                          accent={SalvBannerAccent.STRONG}
                          message={message}
                          type={SalvBannerType.DANGER}
                          className={'m-b-8'}
                        />
                      ))}
                  <FieldArray name='usableFields'>
                    {(arrayHelpers) => (
                      <>
                        <SalvTable
                          columns={columns}
                          columnVisibility={{
                            additionalSchemaFields: type === UsableFieldType.MONITORING,
                            type: type === UsableFieldType.SCREENING,
                            rule: type === UsableFieldType.SCREENING,
                          }}
                          expanded={expanded}
                          data={data}
                          size={Size.SMALL}
                          striped
                          emptyMessage={'Usable fields are not yet defined'}
                          meta={{ arrayHelpers, errors }}
                        >
                          {(row) => {
                            const usableField = row.original;
                            const additionalDataSchemaFields = usableField.additionalSchemaFields?.map(
                              (schemaFieldId) =>
                                schemaFields
                                  .filter((field) => !field.parentId)
                                  .find((schemaField) => schemaField.id === schemaFieldId)!
                            );
                            return (
                              <MonitoringFieldPreview
                                fieldCode={row.original.code}
                                fieldPathParts={
                                  isFormikValue(usableField)
                                    ? usableField.usableFieldParts.map((part) => part.label)
                                    : usableField.path.split(' ')
                                }
                                additionalSchemaFields={additionalDataSchemaFields || []}
                              />
                            );
                          }}
                        </SalvTable>
                        <SalvButton
                          label={'Add field'}
                          icon={faPlus}
                          variant='secondary'
                          size={Size.SMALL}
                          className={'m-t-8'}
                          onClick={() =>
                            arrayHelpers.push({
                              name: '',
                              code: '',
                              usableFieldParts: [],
                              ...(type === UsableFieldType.SCREENING && {
                                type: null,
                                rule: '',
                              }),
                              ...(type === UsableFieldType.MONITORING && {
                                additionalSchemaFields: [],
                              }),
                            })
                          }
                        />
                      </>
                    )}
                  </FieldArray>
                </SalvModal.Body>

                <SalvModal.Footer>
                  <SalvButton label={'Close'} variant='muted' onClick={() => handleClose(resetForm)} />
                  <SalvButton
                    label={'Save'}
                    disabled={Boolean(isSubmitting || Object.keys(errors).length)}
                    type='submit'
                    variant='primary'
                  />
                </SalvModal.Footer>
              </Form>
            </SalvModal>
          </>
        );
      }}
    </Formik>
  );
};

export default UsableFieldsModal;
